import { useState, useEffect, useRef } from 'react'
import '../styles/ChatWidget.css'

interface ChatMessage {
  id: string
  playerNumber: number
  playerName: string
  message: string
  timestamp: number
  isReaction?: boolean
}

interface ChatWidgetProps {
  sessionCode: string
  playerNumber: number
  playerName: string
  otherPlayerName?: string
  sendMessage: (message: string) => void
  onMessageReceived?: (messageCount: number) => void
  mode?: 'floating' | 'embedded' // floating for gameplay, embedded for lobby
}

const QUICK_REACTIONS = [
  { emoji: '👍', label: 'thumbs up' },
  { emoji: '❤️', label: 'heart' },
  { emoji: '😂', label: 'laugh' },
  { emoji: '🎉', label: 'party' },
  { text: 'GG', label: 'good game' },
  { text: 'Nice!', label: 'nice' },
]

export function ChatWidget({
  sessionCode,
  playerNumber,
  playerName,
  otherPlayerName = 'Opponent',
  sendMessage,
  onMessageReceived,
  mode = 'floating'
}: ChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(mode === 'embedded') // Embedded = always open
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [inputValue, setInputValue] = useState('')
  const [isMobile, setIsMobile] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Auto-scroll to latest message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Poll for messages from database
  useEffect(() => {
    if (!sessionCode) return

    const pollMessages = async () => {
      try {
        const response = await fetch(`/api/sessions/${sessionCode}/chat`)
        if (!response.ok) return

        const data = await response.json()
        const newMessages: ChatMessage[] = data.messages.map((msg: any) => ({
          id: msg.id,
          playerNumber: msg.player_number,
          playerName: msg.player_number === playerNumber ? playerName : otherPlayerName,
          message: msg.message,
          timestamp: new Date(msg.created_at).getTime(),
          isReaction: msg.message.length <= 10 && (msg.message.match(/[\p{Emoji}]/u) || ['GG', 'Nice!'].includes(msg.message))
        }))

        // Count new unread messages (only when chat is closed)
        if (!isOpen && newMessages.length > messages.length) {
          const newCount = newMessages.length - messages.length
          setUnreadCount(prev => prev + newCount)

          // Vibrate on new message (mobile)
          if ('vibrate' in navigator && newCount > 0) {
            navigator.vibrate(100)
          }

          onMessageReceived?.(newCount)
        }

        setMessages(newMessages)
      } catch (error) {
        console.error('[ChatWidget] Failed to poll messages:', error)
      }
    }

    pollMessages()
    const interval = setInterval(pollMessages, 2000) // Poll every 2s
    return () => clearInterval(interval)
  }, [sessionCode, playerNumber, playerName, otherPlayerName, isOpen, messages.length, onMessageReceived])

  const handleSend = async (text?: string) => {
    const messageText = text || inputValue.trim()
    if (!messageText) return

    try {
      // Send to database
      await fetch(`/api/sessions/${sessionCode}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_number: playerNumber,
          message: messageText
        })
      })

      // Also send via PeerJS if available (instant delivery)
      sendMessage(messageText)

      // Clear input
      setInputValue('')

      // Re-focus input on desktop
      if (!isMobile) {
        inputRef.current?.focus()
      }
    } catch (error) {
      console.error('[ChatWidget] Failed to send message:', error)
    }
  }

  const handleQuickReaction = (reaction: { emoji?: string; text?: string }) => {
    const message = reaction.emoji || reaction.text!
    handleSend(message)
  }

  const handleOpen = () => {
    setIsOpen(true)
    setUnreadCount(0)

    // Auto-focus input on desktop
    if (!isMobile) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }

  const handleClose = () => {
    if (mode === 'embedded') return // Can't close embedded mode
    setIsOpen(false)
  }

  // Embedded mode (lobby)
  if (mode === 'embedded') {
    return (
      <div className="chat-embedded">
        <div className="chat-header">
          <span className="chat-title">💬 Chat</span>
        </div>

        <div className="chat-messages">
          {messages.length === 0 && (
            <div className="chat-empty">
              <span className="chat-empty-icon">💬</span>
              <span className="chat-empty-text">Say hello to start the conversation!</span>
            </div>
          )}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`chat-message ${msg.playerNumber === playerNumber ? 'mine' : 'theirs'} ${msg.isReaction ? 'reaction' : ''}`}
            >
              <div className="chat-message-header">
                <span className="chat-message-author">{msg.playerName}</span>
                <span className="chat-message-time">
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="chat-message-text">{msg.message}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-quick-reactions">
          {QUICK_REACTIONS.map((reaction, idx) => (
            <button
              key={idx}
              className="reaction-btn"
              onClick={() => handleQuickReaction(reaction)}
              title={reaction.label}
            >
              {reaction.emoji || reaction.text}
            </button>
          ))}
        </div>

        <div className="chat-input-container">
          <input
            ref={inputRef}
            type="text"
            className="chat-input"
            placeholder="Type a message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          />
          <button
            className="chat-send-btn"
            onClick={() => handleSend()}
            disabled={!inputValue.trim()}
          >
            Send
          </button>
        </div>
      </div>
    )
  }

  // Floating mode (gameplay)
  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button className="chat-floating-btn" onClick={handleOpen} aria-label="Open chat">
          💬
          {unreadCount > 0 && (
            <span className="chat-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="chat-backdrop" onClick={handleClose} />

          {/* Panel */}
          <div className={`chat-panel ${isMobile ? 'mobile' : 'desktop'}`}>
            <div className="chat-panel-header">
              <span className="chat-panel-title">💬 Chat</span>
              <button className="chat-close-btn" onClick={handleClose} aria-label="Close chat">
                ✕
              </button>
            </div>

            <div className="chat-messages">
              {messages.length === 0 && (
                <div className="chat-empty">
                  <span className="chat-empty-icon">💬</span>
                  <span className="chat-empty-text">No messages yet</span>
                </div>
              )}
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`chat-message ${msg.playerNumber === playerNumber ? 'mine' : 'theirs'} ${msg.isReaction ? 'reaction' : ''}`}
                >
                  <div className="chat-message-header">
                    <span className="chat-message-author">{msg.playerName}</span>
                    <span className="chat-message-time">
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="chat-message-text">{msg.message}</div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-quick-reactions">
              {QUICK_REACTIONS.map((reaction, idx) => (
                <button
                  key={idx}
                  className="reaction-btn"
                  onClick={() => handleQuickReaction(reaction)}
                  title={reaction.label}
                >
                  {reaction.emoji || reaction.text}
                </button>
              ))}
            </div>

            <div className="chat-input-container">
              <input
                ref={inputRef}
                type="text"
                className="chat-input"
                placeholder="Type a message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              />
              <button
                className="chat-send-btn"
                onClick={() => handleSend()}
                disabled={!inputValue.trim()}
              >
                Send
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}
